<script>
/* eslint-disable semi */
/* eslint-disable no-irregular-whitespace */
import { Vue, Component } from 'vue-property-decorator';
import moment from 'moment';
import { RoutesMap } from '@/router';

const debug = process.env.NODE_ENV === 'development';

@Component
export default class AppComponent extends Vue {
  /**
   * SSR対応 LifeCycleイベント
   */
  serverPrefetch () {
    return this.initialize() || Promise.resolve();
  }

  /**
   * LifeCycleイベント
   * コンポーネントがマウントされたタイミングで初期化処理を実行する
   */
  mounted () {
    // 初期化処理
    this.initialize();
  }

  /**
   * 初期化処理
   */
  initialize () {
    if (debug) {
      console.debug('AppComponent initialize called.');
    }
  }

  /**
   * app-eventを親コンポーネントへの透過イベントとする
   */
  onAppEvent (...args) {
    this.$emit('app-event', ...args);
  }

  /**
   * 空のイベントハンドラ
   */
  emptyEventHandler () {
    console.log('emptyEventHandler called');
  }

  /**
   * 画面遷移
   * Ctrlキー押下状態では新タブ表示します
   *
   * @click でハンドルすると正常に動作しません。
   * @click.native でハンドルして第二引数で$eventを引き渡す必要がありますので注意。
   *
   */
  go (dist, event) {
    if (event && event.ctrlKey) {
      this.openTab(dist);
    } else {
      var route = this.convertRoute(dist);
      this.$router.push(route);
    }
  }

  /**
   * DATA NOT FOUND エラーページへ遷移
   */
  goDataNotFoundErrorPage () {
    this.$router.replace({
      name: 'U999999',
      query: {
        status: '320'
      }
    });
  }

  /**
   * 新タブ表示
   */
  openTab (dist) {
    var route = this.convertRoute(dist);
    window.open(this.$router.resolve(route).href, '_blank');
  }

  convertRoute (dist) {
    var route = Object.assign({}, dist);

    if (route && ('name' in route) && (!route.absolute)) {
      route.name = this.convertRouteName(route.name);
    }

    // queryに文字列以外の値が指定されている場合 文字列に変換する
    if (route && ('query' in route)) {
      const query = {};
      for (let key in route.query) {
        const val = route.query[key];
        if (val === null || val === undefined) {
          query[key] = null;
        } else {
          query[key] = String(val);
        }
      }
      route.query = query;
    }

    return route;
  }

  /**
   * 名前付きルートを補正する
   */
  convertRouteName (name) {
    if ('name' in this.$route) {
      // 下位階層
      const childName = this.$route.name + '/' + name;
      if (childName in RoutesMap) {
        return childName;
      }

      // 兄弟階層
      const current = RoutesMap[this.$route.name];
      if (current !== null && current !== undefined) {
        const parent = current.parent;
        if (parent !== null && parent !== undefined) {
          const brosName = parent + '/' + name;
          if (brosName in RoutesMap) {
            return brosName;
          }
        }
      }

      // 元の指定名
      return name;
    } else {
      return name;
    }
  }

  /**
   * アプリケーション共通 ユーティリティ関数
   * ※get での応答値はキャッシュされるので拡張する際には注意
   */
  get appUtils () {
    // thisを参照する関数
    // 日付文字列からYYYYMM形式の文字列へ変換
    const dateToYm = (value) => {
      let date1 = null;
      let date2 = null;

      try {
        date1 = moment(value, 'YYYY/MM/DD').toDate();
      } catch (e) {}
      try {
        date2 = moment(value, 'YYYY/MM/DD HH:mm:ss').toDate();
      } catch (e) {}

      const date = (date1 || date2);

      if (date) {
        const y = date.getFullYear();
        const m = date.getMonth() + 1;
        return ('0000' + String(y)).substr(-4) + ('00' + String(m)).substr(-2);
      }

      return null;
    }

    const pathOf = (name) => {
      const convertedRouteName = this.convertRouteName(name);
      if (convertedRouteName in RoutesMap) {
        return RoutesMap[convertedRouteName].path;
      } else {
        console.log('pathOf() called with invalid name ' + name);
        return null;
      }
    }

    return {
      /**
       * nullまたはundefined、空の配列・オブジェクトか判定します
       */
      isEmpty: function (src) {
        if ((src === null) || (src === undefined) || (src === '')) {
          return true;
        } else if (Array.isArray(src) && src.length === 0) {
          return true;
        } else if ((typeof src) === 'object') {
          return Object.keys(src).length === 0;
        } else {
          return false;
        }
      },

      /**
       * 名前付きルーター設定のパスを取得する
       */
      pathOf,

      /**
       * 複数のデータを結合してID文字列を生成する
       */
      joinId: function (...args) {
        if ((args === null) || (args === undefined)) {
          return null;
        }
        return args.join(':');
      },

      /**
       * joinIdで結合されたIDを分割する
       * 第二引数以降でプロパティ名を指定する
       * 例1) 引数が ('1:2', 'key1', 'key2')の場合
       * { key1: '1', key2: '2' } という連想配列を返却する
       * 例2) 引数が ('1', 'key1', 'key2')の場合
       * { key1: '1', key2: null } という連想配列を返却する
       */
      splitId: function (id, ...args) {
        // id 値を分割して配列に変換する
        let vals;
        if ((args === null) || (args === undefined)) {
          vals = [];
        } else {
          vals = id.split(':');
        }

        // 返却用の連想配列を生成する
        const result = {};
        args.forEach(key => {
          const tmpVal = vals.shift();
          result[key] = (tmpVal === undefined) ? null : tmpVal;
        })
        return result;
      },

      /**
       * 画面内でソート処理を行う
       * modelには、listとsortBy属性が含まれている必要があります。
       * ※一般的な検索APIからの取得データにはそれらが含まれています。
       */
      tableSort: function (model, sort) {
        const desc = (model.sortBy === sort + ':asc');
        model.sortBy = sort + ((desc) ? ':desc' : ':asc');
        model.list.sort((row1, row2) => {
          if (row1[sort] > row2[sort]) {
            return (desc) ? -1 : 1;
          } else {
            return (desc) ? 1 : -1;
          }
        });
      },

      /**
       * 値が空の場合0に補正する
       */
      nvl0 (value) {
        if ((value === null) || (value === undefined)) {
          return 0;
        } else {
          return value;
        }
      },

      /**
       * 日付をYYYYMM形式の文字列に変換する
       */
      dateToYm,

      /**
       * テーブルの最大高
       * スクロールした場合でもヘッダが画面外にならないよう、
       * 画面高さからヘッダ・フッタ部の高さ＋マージンを減じた高さとする。
       */
      get tableMaxHeight () {
        return window.innerHeight - 146
      },

      /**
       * ブラウザ種別判定
       */
      get browser () {
        const userAgent = window.navigator.userAgent.toLowerCase();
        if (userAgent.indexOf('msie') !== -1 || userAgent.indexOf('trident') !== -1) {
          return 'IE';
        } else if (userAgent.indexOf('edge') !== -1) {
          return 'Edge';
        } else if (userAgent.indexOf('chrome') !== -1) {
          return 'Chrome';
        } else if (userAgent.indexOf('safari') !== -1) {
          return 'Safari';
        } else if (userAgent.indexOf('firefox') !== -1) {
          return 'FireFox';
        } else if (userAgent.indexOf('opera') !== -1) {
          return 'Opera';
        } else {
          return 'Unknown';
        }
      },

      /**
       * 英数字半角変換
       */
      toHalfWidth (arg) {
        if (arg === null || arg === undefined) {
          return '';
        }
        arg = String(arg);
        arg = arg.replace(/　/g, ' ');
        arg = arg.replace(/￥/g, '\\');
        arg = arg.replace(/（/g, '(');
        arg = arg.replace(/）/g, ')');
        arg = arg.replace(/［/g, '[');
        arg = arg.replace(/］/g, ']');
        return arg.replace(/[Ａ-Ｚａ-ｚ０-９＋＊－＿＠＆！＃．，／]/g,
          function (s) {
            return String.fromCharCode(s.charCodeAt(0) - 0xFEE0);
          }
        );
      },

      /**
       * 英数字全角変換
       */
      toDoubleWidth (arg) {
        if (arg === null || arg === undefined) {
          return '';
        }
        arg = String(arg);
        arg = arg.replace(/ /g, '　');
        arg = arg.replace(/\(/g, '（');
        arg = arg.replace(/\)/g, '）');
        arg = arg.replace(/\[/g, '［');
        arg = arg.replace(/\]/g, '］');
        return arg.replace(/[A-Za-z0-9+*\\\-_@&!#\\.,\\/]/g,
          function (s) {
            return String.fromCharCode(s.charCodeAt(0) + 0xFEE0);
          }
        );
      }
    }
  }
  focus () {
    if (this.$refs.input && this.$refs.input.focus) {
      this.$refs.input.focus();
    }
  }
  blur () {
    if (this.$refs.input && this.$refs.input.blur) {
      this.$refs.input.blur();
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/app/AppComponent.vue